import { ancanaApi } from "./ancanaApi";
import {
  ICheckResult,
  ICopyDraftReport,
  ICopyEditorState,
  ICopyField,
  ICopyTranslateReport,
  IDriveFolderRow,
  IDriveFolders,
  IAmenityPick,
  IChecklist,
  ISlideOutline,
  IGalleryPicker,
  IMonitorRow,
  IPresentationEvent,
  IListingMaterials,
  IPresentation,
  IMapPreview,
  IPresentationTemplate,
  ITemplateDetail,
} from "../types/models/presentations";

/**
 * Sales materials API.
 *
 * Everything here goes through Django; the Gutenberg engine is internal and the
 * browser never talks to it directly.
 */

/** Photos as repeated `files` keys, the way the upload endpoints read them. */
export const photoForm = (files: File[]) => {
  const body = new FormData();
  files.forEach((file) => body.append("files", file));
  return body;
};

export const presentationsApi = {
  /** Every material for one listing: template, why it was chosen, readiness. */
  forListing: (listingId: number) =>
    ancanaApi
      .get<IListingMaterials>(`presentations/for-listing/${listingId}/`)
      .then((r) => r.data),

  /**
   * Queue a generation. Returns immediately with a PENDING presentation; the
   * caller polls, since a real brochure takes tens of seconds to fill and convert.
   */
  generate: (listing: number, namespace: string, language: string) =>
    ancanaApi
      .post<IPresentation & { task_id?: string }>("presentations/generate/", {
        listing,
        namespace,
        language,
      })
      .then((r) => r.data),

  get: (id: number) =>
    ancanaApi.get<IPresentation>(`presentations/${id}/`).then((r) => r.data),

  /** The exact data that would be injected — for "why is this slide blank?" */
  payload: (listingId: number, lang: string) =>
    ancanaApi
      .get<Record<string, any>>(`presentations/payload/${listingId}/?lang=${lang}`)
      .then((r) => r.data),

  /**
   * The listing's map at the given parameters, without saving them.
   *
   * Read-only on purpose: someone can try zoom levels without changing what the
   * next brochure uses. Trying a setting and then saving it costs one vendor
   * fetch between them, because the staged image is keyed on the parameters
   * themselves — so going back to a zoom already tried is instant.
   */
  mapPreview: (listingId: number, params: { zoom?: number; map_type?: string }) =>
    ancanaApi
      .get<IMapPreview>(`presentations/map/${listingId}/preview/`, { params })
      .then((r) => r.data),

  /** Persist the overrides. Null zoom means "follow the global default". */
  saveMapSettings: (
    listingId: number,
    body: { zoom?: number | null; map_type?: string }
  ) =>
    ancanaApi
      .patch<{ zoom: number | null; map_type: string; url: string | null }>(
        `presentations/map/${listingId}/`,
        body
      )
      .then((r) => r.data),

  /** The listing's gallery slots, the photos that may fill them, and the picks. */
  galleryPicker: (listingId: number) =>
    ancanaApi
      .get<IGalleryPicker>(`presentations/gallery/${listingId}/`)
      .then((r) => r.data),

  /**
   * Replace the listing's gallery-slot picks. The whole set, not a patch:
   * swapping two photos is one save rather than two that pass through a
   * duplicate. The server replaces only the kind it is sent, so the amenity
   * grid's picks are kept.
   */
  saveGalleryPicks: (listingId: number, picks: Record<string, string | null>) =>
    ancanaApi
      .put<IGalleryPicker>(`presentations/gallery/${listingId}/`, { picks })
      .then((r) => r.data),

  /**
   * Upload photos to the listing's Brochure gallery — off the website and out
   * of the default order. Answers the picker, plus the new photos' refs.
   */
  uploadGalleryPhotos: (listingId: number, files: File[]) =>
    ancanaApi
      .post<IGalleryPicker & { uploaded: string[] }>(
        `presentations/gallery/${listingId}/upload/`, photoForm(files),
        { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data),

  /** The same for the amenity grid's picks, captions included; gallery picks are kept. */
  saveAmenityPicks: (listingId: number, amenityPicks: Record<string, IAmenityPick | null>) =>
    ancanaApi
      .put<IGalleryPicker>(`presentations/gallery/${listingId}/`, { amenity_picks: amenityPicks })
      .then((r) => r.data),

  /** Every presentation of an active template, with where Drive stands. */
  monitor: () =>
    ancanaApi.get<IMonitorRow[]>("presentations/monitor/").then((r) => r.data),

  /** What happened to one presentation, newest first. */
  events: (id: number) =>
    ancanaApi
      .get<IPresentationEvent[]>(`presentations/${id}/events/`)
      .then((r) => r.data),

  /** Queue a send now rather than waiting for the next render. */
  sendToDrive: (id: number) =>
    ancanaApi
      .post<{ task_id: string }>(`presentations/${id}/send-to-drive/`, {})
      .then((r) => r.data),

  driveFolders: () =>
    ancanaApi.get<IDriveFolders>("presentations/drive/folders/").then((r) => r.data),

  /** Propose folders for listings nobody has decided on. Never confirms. */
  discoverDriveFolders: () =>
    ancanaApi
      .post<{ suggested: number; not_found: number; kept: number }>(
        "presentations/drive/discover/", {})
      .then((r) => r.data),

  /** A person's decision: this folder, or never send this listing. */
  setDriveFolder: (
    listingId: number,
    body: {
      status: "confirmed" | "disabled";
      folder_id?: string;
      folder_name?: string;
      /** Make a folder named after the listing (or reuse one already called that). */
      create?: boolean;
    }
  ) =>
    ancanaApi
      .put<IDriveFolderRow & { created?: boolean }>(
        `presentations/drive/folder/${listingId}/`, body)
      .then((r) => r.data),

  vocabulary: () =>
    ancanaApi
      .get<{ paths: string[]; list_slot_count: number; canvas_types: string[] }>(
        "presentations/vocabulary/"
      )
      .then((r) => r.data),
};

/**
 * Authored copy — the half of a brochure's text nobody can derive.
 *
 * Declarations are global, not per template: templates that draw the same
 * slide share the copy on it.
 */
export const copyFieldsApi = {
  list: (params: Record<string, any> = {}) =>
    ancanaApi
      .get<{ results: ICopyField[] }>("copy-fields/", {
        params: { page_size: 200, ...params },
      })
      .then((r) => r.data.results ?? (r.data as any)),

  /**
   * Describe the keys a template draws, at import.
   *
   * Keys that already exist come back in `existing`, untouched — two templates
   * sharing a key is the intended case, two authors meaning different things by
   * it is the failure, and silently taking the newer wording would rewrite the
   * first template's brochures with nobody seeing it.
   */
  declare: (fields: Partial<ICopyField>[]) =>
    ancanaApi
      .post<{ created: ICopyField[]; existing: ICopyField[] }>(
        "copy-fields/declare/",
        { fields }
      )
      .then((r) => r.data),

  update: (id: number, patch: Partial<ICopyField>) =>
    ancanaApi
      .patch<ICopyField>(`copy-fields/${id}/`, patch)
      .then((r) => r.data),

  /**
   * Have the co-writer draft this listing's copy in one language. Drafts are
   * saved as `ai_draft`; a person's copy is kept, and `ai: never` lists are left
   * alone. Returns the fresh editor state along with what happened.
   */
  draft: (listingId: number, templateId: number | string, language: string,
          roots?: string[]) =>
    ancanaApi
      .post<{ report: ICopyDraftReport; state: ICopyEditorState }>(
        `copy-fields/listing/${listingId}/${templateId}/draft/`,
        roots ? { language, roots } : { language }
      )
      .then((r) => r.data),

  /**
   * Bring the copy over from `source` into `target`, and save it. A person's
   * copy in `target` is kept; empty items, drafts and earlier translations are
   * replaced. Returns the fresh editor state along with what happened.
   */
  translate: (listingId: number, templateId: number | string, source: string,
              target: string, roots?: string[]) =>
    ancanaApi
      .post<{ report: ICopyTranslateReport; state: ICopyEditorState }>(
        `copy-fields/listing/${listingId}/${templateId}/translate/`,
        roots ? { source, target, roots } : { source, target }
      )
      .then((r) => r.data),

  translateDevelopment: (developmentId: number, source: string, target: string) =>
    ancanaApi
      .post<{ report: ICopyTranslateReport; state: ICopyEditorState }>(
        `copy-fields/development/${developmentId}/translate/`, { source, target })
      .then((r) => r.data),

  /** Every field the template draws, with its value in every language. */
  forListing: (listingId: number, templateId: number | string) =>
    ancanaApi
      .get<ICopyEditorState>(`copy-fields/listing/${listingId}/${templateId}/`)
      .then((r) => r.data),

  /** A development's slide copy (`copy.development.*`), every language. */
  forDevelopment: (developmentId: number) =>
    ancanaApi
      .get<ICopyEditorState>(`copy-fields/development/${developmentId}/`)
      .then((r) => r.data),

  draftDevelopment: (developmentId: number, language: string) =>
    ancanaApi
      .post<{ report: ICopyDraftReport; state: ICopyEditorState }>(
        `copy-fields/development/${developmentId}/draft/`, { language })
      .then((r) => r.data),

  saveDevelopment: (
    developmentId: number,
    values: { key: string; language: string; text: string }[]
  ) =>
    ancanaApi
      .post<{ saved: number }>(`copy-fields/development/${developmentId}/`, { values })
      .then((r) => r.data),

  /** Saving is the review step, so everything written here counts as human. */
  save: (
    listingId: number,
    values: { key: string; language: string; text: string }[]
  ) =>
    ancanaApi
      .post<{ saved: number }>(`copy-fields/listing/${listingId}/`, { values })
      .then((r) => r.data),
};

export const templatesApi = {
  list: (params: Record<string, any> = {}) =>
    ancanaApi
      .get<{ results: IPresentationTemplate[] }>("presentation-templates/", {
        params: { page_size: 100, ...params },
      })
      .then((r) => r.data.results ?? (r.data as any)),

  /** Scan a .pptx without storing it. */
  check: (file: File) => {
    const body = new FormData();
    body.append("file", file);
    return ancanaApi
      .post<ICheckResult>("presentation-templates/check/", body, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },

  upload: (file: File, fields: Record<string, any>) => {
    const body = new FormData();
    body.append("file", file);
    Object.entries(fields).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      // A list has to go as repeated keys: DRF's ListField reads a multipart
      // body with getlist(), so a joined "1,2,3" arrives as one bad integer.
      if (Array.isArray(value)) {
        value.forEach((item) => body.append(key, String(item)));
        return;
      }
      body.append(key, String(value));
    });
    return ancanaApi
      .post<{ template: IPresentationTemplate } & ICheckResult>(
        "presentation-templates/upload/",
        body,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((r) => r.data);
  },

  update: (id: number, patch: Partial<IPresentationTemplate>) =>
    ancanaApi
      .patch<IPresentationTemplate>(`presentation-templates/${id}/`, patch)
      .then((r) => r.data),

  /** Everything behind one template: who it covers, what has been rendered. */
  detail: (id: number) =>
    ancanaApi
      .get<ITemplateDetail>(`presentation-templates/${id}/presentations/`)
      .then((r) => r.data),

  /** Everything this template still needs from one property, step by step. */
  checklist: (id: number, listingId: number) =>
    ancanaApi
      .get<IChecklist>(`presentation-templates/${id}/checklist/${listingId}/`)
      .then((r) => r.data),

  /** The same, one slide at a time: what each slide asks, and its thumbnail page. */
  slides: (id: number, listingId: number) =>
    ancanaApi
      .get<ISlideOutline>(`presentation-templates/${id}/slides/${listingId}/`)
      .then((r) => r.data),

  /** Queue the properties with nothing, a failure, or stale data. */
  generateMissing: (id: number) =>
    ancanaApi
      .post<{
        queued: number;
        listings: number[];
        /** Rows skipped, with the checklist steps blocking each. */
        blocked: { id: number; name: string; blocking: string[] }[];
      }>(
        `presentation-templates/${id}/generate-missing/`,
        {}
      )
      .then((r) => r.data),

  /**
   * Render the whole template to a PDF and return its URL. Built on the first
   * call — about fifteen seconds on a 35 MB deck — and reused afterwards.
   */
  preview: (id: number) =>
    ancanaApi
      .post<{ preview_url: string }>(`presentation-templates/${id}/preview/`, {})
      .then((r) => r.data.preview_url),

  revalidate: (id: number) =>
    ancanaApi
      .post<{ template: IPresentationTemplate; validation: any }>(
        `presentation-templates/${id}/revalidate/`,
        {}
      )
      .then((r) => r.data),
};
